import isEmpty from 'lodash/isEmpty';
import { JobSpeciality, JobType } from '@clipr/lib';
import { action, computed, makeObservable, observable, when } from 'mobx';
import { Store } from '../../store/store';
import { BindingProps, StoreNode } from '../../store';
import { input, inputGroup, InputGroupState, InputState } from '../../components/input';
import { getDefaultEnrichmentLevel, getEnrichmentLevelItemTooltip, getEnrichmentLevelLabel, JobLevel } from '../../entities/job/jobFeatures';
import { JobSynchronizer } from '../../entities/job/jobSynchronizer';
import { DefaultSpecialityItem, getJobSpecialityInput, isDualPlusPublicSafetySpeciality, parseCustomSpeciality, SpecialityItems, getSpecialityItemsWithCustom, getTeamDefaultJobSpecialityInput } from '../../entities/job/jobSpeciality';
import { DefaultLanguageItem, getDefaultLanguageValue, getLanguageInputItem, LanguageItems } from '../../entities/language';
import { UploadTask } from '../../services/upload/uploadTask';
import { UploadFile } from '../../services/upload/uploadService';
import { jobIdFromToken, recordingLocationFromToken } from '../../services/upload/uploadToken';
import { JobVideoTypeList, Team } from '../../entities';
import { inferPublicSafetyCaseIdFromFilename, inferPublicSafetyOfficerIdentityFromFilename } from './publicSafetyCaseId';

type UploadFileItemProps = BindingProps<{
  teamId?: string;
  jobId?: string;
  /** Segments of a single recording, in playback order; a lone file for an ordinary upload. */
  files: File[];
  /** Title for a merged recording, when the files are segments of one. */
  mergedTitle?: string;
}>;

export class UploadFileItemState
  extends StoreNode {

  constructor(store: Store, props: UploadFileItemProps) {
    super(store, props);
    makeObservable(this, {
      task: observable,
      file: computed,
      isMerged: computed,
      isStaged: computed,
      isActive: computed,
      isCompleted: computed,
      isFailed: computed,
      showInStaging: computed,
      inProgress: computed,
      teamId: computed,
      team: computed,
      jobId: computed,
      job: computed,
      isSpecialityInputDisabled: computed,
      isDualPlusPublicSafetyAvailable: computed,
      isPublicSafetyCaseIdVisible: computed,
      submit: action,
      init: action,
    });

    this.levelInput = input(this, {
      name: 'level',
      placeholder: 'Choose an option',
      selectorItems: () => JobLevel.map(item => ({
        value: item,
        label: getEnrichmentLevelLabel(item),
        tooltip: getEnrichmentLevelItemTooltip(item)
      })),
      showStatusMessage: true,
      statusMessage: (input: InputState) =>
        input.status === 'error' && input.showStatusIcons ? input.error?.toString() : '',
      export: (self: InputState) => self.normValue,
      disabled: () => !!this.jobId
    });

    this.specialityInput = input(this, {
      name: 'speciality',
      placeholder: 'Choose an option',
      selectorItems: () => {
        const uploadPage = this.store.uploadPage;
        return getSpecialityItemsWithCustom(uploadPage.customSpecialties);
      },
      showStatusMessage: true,
      isRequired: () => !this.isSpecialityInputDisabled,
      // statusMessage: (input: InputState) =>
      //  input.status === 'error' && input.showStatusIcons ? input.error?.toString() : '',
      error: (input: InputState) => {
        if (isEmpty(input.value) && !this.isSpecialityInputDisabled) return 'Required field';
      },
      export: (self: InputState) => self.normValue,
      disabled: () => !!this.jobId,
      onChange: (input: InputState) => {
        if (input.normValue === 'Medical') {
          const lang = getLanguageInputItem(getDefaultLanguageValue());
          this.languageInput.loadValue(lang);
        }
      }
    });

    this.languageInput = input(this, {
      name: 'language',
      placeholder: 'Choose an option',
      selectorItems: LanguageItems,
      showStatusMessage: true,
      isRequired: true,
      statusMessage: (input: InputState) =>
        input.status === 'error' && input.showStatusIcons ? input.error?.toString() : '',
      error: (input: InputState) => {
        if (isEmpty(input.value)) return 'Required field';
        if (this.specialityInput.normValue === 'Medical' && input.normValue !== 'en-US')
          return 'Only US English supported';
      },
      export: (self: InputState) => self.normValue,
      disabled: () => !!this.jobId || this.specialityInput.normValue === 'Medical',
    });

    this.videoTypeInput = input(this, {
      name: 'videoType',
      placeholder: 'Choose an option',
      selectorItems: JobVideoTypeList,
      showStatusMessage: true,
      export: (self: InputState) => self.normValue,
      disabled: () => !!this.jobId
    });

    this.publicSafetyCaseIdInput = input(this, {
      name: 'publicSafetyCaseId',
      label: 'Case ID',
      placeholder: 'Case ID',
      showStatusMessage: true,
      export: (self: InputState) => self.value?.trim() || undefined,
      disabled: () => !!this.jobId
    });

    this.publicSafetyOfficerNameInput = input(this, {
      name: 'publicSafetyOfficerName',
      label: 'Dual+ Officer Name',
      placeholder: 'Officer name',
      showStatusMessage: true,
      export: (self: InputState) => self.value?.trim() || undefined,
      disabled: () => !!this.jobId
    });

    this.publicSafetyOfficerTitleInput = input(this, {
      name: 'publicSafetyOfficerTitle',
      label: 'Officer Title',
      placeholder: 'Officer title',
      showStatusMessage: true,
      export: (self: InputState) => self.value?.trim() || undefined,
      disabled: () => !!this.jobId
    });

    this.generateDualPlusDraftInput = input(this, {
      name: 'generateDualPlusDraft',
      label: 'Generate Dual+ first draft',
      disabled: () => !!this.jobId
    });

    this.form = inputGroup(this, {
      name: 'languages',
      inputs: [
        this.specialityInput,
        this.languageInput,
        this.levelInput,
        this.videoTypeInput,
        this.generateDualPlusDraftInput,
        this.publicSafetyCaseIdInput,
        this.publicSafetyOfficerNameInput,
        this.publicSafetyOfficerTitleInput
      ],
      isSubmitDisabled: () => {
        return (
          this.form.isSubmitting ||
          this.form.hasTouchedErrorInputs
        );
      }
    });

    this.files = this.props.files;
    this.mergedTitle = this.props.mergedTitle;
    this.init();
  }

  task: UploadTask | null = null;

  readonly form: InputGroupState;
  readonly files: File[];
  readonly mergedTitle?: string;

  /** Representative file for display; segments beyond the first share its settings. */
  get file(): File {
    return this.files[0];
  }

  get isMerged(): boolean {
    return this.files.length > 1;
  }

  readonly languageInput: InputState;
  readonly levelInput: InputState;
  readonly specialityInput: InputState;
  readonly videoTypeInput: InputState;
  readonly generateDualPlusDraftInput: InputState;
  readonly publicSafetyCaseIdInput: InputState;
  readonly publicSafetyOfficerNameInput: InputState;
  readonly publicSafetyOfficerTitleInput: InputState;


  get isStaged(): boolean {
    return !this.task;
  }

  get isActive(): boolean {
    return !!this.task?.isActive;
  }

  get isCompleted(): boolean {
    return !!this.task?.isCompleted;
  }

  get isFailed(): boolean {
    return !!this.task?.isFailed;
  }

  get showInStaging(): boolean {
    return this.isStaged;
  }

  get inProgress(): boolean {
    return this.isActive || this.isFailed;
  }

  // #region Resolved props
  get teamId(): string | null {
    return this.resolvedProps.teamId;
  }

  get team(): Team | null {
    return this.store.teamManager.getTeam(this.teamId);
  }

  get jobId(): string | null {
    return this.resolvedProps.jobId;
  }
  // #endregion

  get job() {
    return this.store.jobManager.maybeGetJob(this.jobId);
  }

  get isSpecialityInputDisabled() {
    return this.levelInput?.value === 'media';
  }

  get isDualPlusPublicSafetyAvailable() {
    const customParsed = parseCustomSpeciality(this.specialityInput.normValue);
    return this.team?.dualPlusActive === true
      && isDualPlusPublicSafetySpeciality(customParsed?.speciality ?? this.specialityInput.normValue);
  }

  get isPublicSafetyCaseIdVisible() {
    return this.isDualPlusPublicSafetyAvailable && !!this.generateDualPlusDraftInput.value;
  }

  /**
   * Starts the upload. `onTask` receives every task as it is created — a merged recording only
   * knows the job id after its first segment lands, so its later tasks appear asynchronously.
   */
  submit(onTask: (task: UploadTask) => void) {
    const formData = this.form.export();
    const customParsed = parseCustomSpeciality(this.specialityInput.normValue);

    const jobFields: Omit<UploadFile, 'file'> = {
      languageCode: formData.language || undefined,
      jobLevel: formData.level || undefined,
      jobSpeciality: customParsed?.speciality ?? formData.speciality as JobSpeciality ?? undefined,
      customSpecialtyId: customParsed?.customSpecialtyId,
      publicSafetyCaseId: this.isPublicSafetyCaseIdVisible
        ? this.publicSafetyCaseIdInput.value?.trim() || undefined
        : undefined,
      publicSafetyOfficerName: this.isPublicSafetyCaseIdVisible
        ? this.publicSafetyOfficerNameInput.value?.trim() || undefined
        : undefined,
      publicSafetyOfficerTitle: this.isPublicSafetyCaseIdVisible
        ? this.publicSafetyOfficerTitleInput.value?.trim() || undefined
        : undefined,
      videoType: formData.videoType as JobType,
    };

    if (!this.isMerged) {
      this.task = this.store.uploadService.createTask({ ...jobFields, file: this.file }, {
        teamId: this.teamId || undefined,
        jobId: this.jobId || undefined
      });
      onTask(this.task);
      return;
    }

    // Nothing awaits this, so a throw would surface only as an unhandled rejection and the item
    // would sit there looking busy forever.
    void this.submitSegments(jobFields, onTask).catch(() => this.notifySegmentsFailed());
  }

  /**
   * Uploads every segment under one job id, then starts a single job that stitches them together.
   * Mirrors the multi-document warrant upload: the first file mints the job id, the rest follow.
   */
  private async submitSegments(jobFields: Omit<UploadFile, 'file'>, onTask: (task: UploadTask) => void) {
    const teamId = this.teamId || undefined;

    const [firstTask] = this.store.uploadService.createTasks(
      [{ ...jobFields, file: this.files[0], uploadOnly: true, partIndex: 0 }],
      { teamId },
    );
    this.task = firstTask;
    onTask(firstTask);

    await when(() => firstTask.isCompleted || firstTask.isFailed);

    const firstToken = firstTask.uploadedToken;
    const jobId = jobIdFromToken(firstToken);
    if (firstTask.isFailed || !firstToken || !jobId) {
      this.notifySegmentsFailed();
      return;
    }

    const restTasks = this.store.uploadService.createTasks(
      this.files.slice(1).map((file, index) => ({
        ...jobFields,
        file,
        uploadOnly: true,
        partIndex: index + 1,
      })),
      { teamId, jobId },
    );
    restTasks.forEach(onTask);

    await Promise.all(restTasks.map(task => when(() => task.isCompleted || task.isFailed)));

    if (restTasks.some(task => task.isFailed)) {
      this.notifySegmentsFailed();
      return;
    }

    const inputRecordingLocations = [firstTask, ...restTasks]
      .map(task => recordingLocationFromToken(task.uploadedToken));

    // Starting the job with a short list would stitch an incomplete recording and report it as whole.
    if (inputRecordingLocations.some(location => !location)) {
      this.notifySegmentsFailed();
      return;
    }

    const synchronizer = new JobSynchronizer({
      teamId,
      jobData: {
        title: this.mergedTitle,
        languageCode: jobFields.languageCode,
        enrichmentLevel: jobFields.jobLevel,
        speciality: jobFields.jobSpeciality,
        customSpecialtyId: jobFields.customSpecialtyId,
        publicSafetyCaseId: jobFields.publicSafetyCaseId,
        publicSafetyOfficerName: jobFields.publicSafetyOfficerName,
        publicSafetyOfficerTitle: jobFields.publicSafetyOfficerTitle,
        videoType: jobFields.videoType,
        inputRecordingLocations: inputRecordingLocations.filter((l): l is string => !!l),
      },
    }, this.store);
    synchronizer.bindToken(firstToken);

    const [, jobErr] = await synchronizer.submitStartJob();
    if (jobErr) {
      this.notifySegmentsFailed();
      return;
    }

    synchronizer.setSynced();
  }

  private notifySegmentsFailed() {
    this.dispatch(
      'NotificationService',
      'notifyError',
      `Could not merge '${this.file.name}' and its other parts.`);
  }

  init = () => {
    const { job, team } = this;

    //init specialityInput
    if (job && job.medicalSpecialty !== undefined) {
      this.specialityInput.loadValue(SpecialityItems.find(item =>
        item.value === getJobSpecialityInput(this.job || null)
      ));
    } else {
      const uploadPage = this.store.uploadPage;
      this.specialityInput.loadValue(
        getTeamDefaultJobSpecialityInput(this.team, uploadPage.customSpecialties)
          ?? this.store.user?.defaultJobSpeciality
          ?? DefaultSpecialityItem?.value
      );
    }

    //init languageInput
    if (job?.languageCode) {
      this.languageInput.loadValue(getLanguageInputItem(job.languageCode));
    } else {
      this.languageInput.loadValue(
        getLanguageInputItem(this.team?.defaultTranscriptLanguage ?? null) ?? DefaultLanguageItem
      );
    }

    //init levelInput
    if (job?.enrichmentLevel) {
      this.levelInput.loadValue(JobLevel.find((value) => value === job.enrichmentLevel));
    } else if (team?.enrichmentLevel) {
      this.levelInput.loadValue(JobLevel.find((value) => value === team.enrichmentLevel));
    } else {
      this.levelInput.loadValue(getDefaultEnrichmentLevel());
    }

    //init videoTypeInput
    if (job?.videoType) {
      this.videoTypeInput.loadValue(JobVideoTypeList.find(item => item.value === job.videoType) ?? JobType.Presentation);
    } else {

      // set default based on team publicSafety setting
      if (team?.publicSafetyActivated)
        this.videoTypeInput.loadValue(JobType.BodyWornCamera);
      else
        this.videoTypeInput.loadValue(JobType.Presentation);
    }

    if (!job) {
      const caseId = inferPublicSafetyCaseIdFromFilename(this.file.name);
      const officer = inferPublicSafetyOfficerIdentityFromFilename(this.file.name);
      if (caseId) {
        this.publicSafetyCaseIdInput.loadValue(caseId);
        this.generateDualPlusDraftInput.loadValue(true);
      }
      if (officer.name) {
        this.publicSafetyOfficerNameInput.loadValue(officer.name);
        this.generateDualPlusDraftInput.loadValue(true);
      }
      if (officer.title) {
        this.publicSafetyOfficerTitleInput.loadValue(officer.title);
        this.generateDualPlusDraftInput.loadValue(true);
      }
    } else if (job.publicSafetyCaseId) {
      this.publicSafetyCaseIdInput.loadValue(job.publicSafetyCaseId);
      this.generateDualPlusDraftInput.loadValue(true);
    }
  }
}
